// Shared Holiday Helper
// Turkish public holidays — static data, no external dependencies
// Fixed holidays + Islamic holidays (Ramazan/Kurban) + arife günleri precomputed 2024–2035
// This file is copied to frontend/src/shared/ and backend/src/shared/ by sync-shared.js
//
// SAAS-COUPLING: takvim Türkiye'nin resmi ve dini tatilleri — hardcoded — çıkarmada
// kiracının ülkesine göre tatil takvimi okunur, saat dilimiyle aynı ayardan beslenir.

// --- Fixed Turkish Public Holidays (month-day) ---
const FIXED_HOLIDAYS = [
    { md: '01-01', name: 'Yılbaşı' },
    { md: '04-23', name: 'Ulusal Egemenlik ve Çocuk Bayramı' },
    { md: '05-01', name: 'Emek ve Dayanışma Günü' },
    { md: '05-19', name: 'Atatürk\'ü Anma, Gençlik ve Spor Bayramı' },
    { md: '07-15', name: 'Demokrasi ve Millî Birlik Günü' },
    { md: '08-30', name: 'Zafer Bayramı' },
    { md: '10-29', name: 'Cumhuriyet Bayramı' },
];

// --- Islamic Holidays (precomputed, Diyanet-based) ---
// Ramazan Bayramı: 3 days, Kurban Bayramı: 4 days
// Start dates by year (first day of each bayram)
// Years with two Ramazan in same Gregorian year use ramazan2
const ISLAMIC_HOLIDAYS: Record<number, { ramazan: string; kurban: string; ramazan2?: string }> = {
    2024: { ramazan: '04-10', kurban: '06-17' },
    2025: { ramazan: '03-30', kurban: '06-06' },
    2026: { ramazan: '03-20', kurban: '05-27' },
    2027: { ramazan: '03-09', kurban: '05-16' },
    2028: { ramazan: '02-27', kurban: '05-04' },
    2029: { ramazan: '02-14', kurban: '04-24' },
    2030: { ramazan: '02-04', kurban: '04-13' },
    2031: { ramazan: '01-24', kurban: '04-02' },
    2032: { ramazan: '01-13', kurban: '03-22' },
    2033: { ramazan: '01-01', kurban: '03-11', ramazan2: '12-22' },
    2034: { ramazan: '12-12', kurban: '02-28' },
    2035: { ramazan: '12-01', kurban: '02-18' },
};

/**
 * Format date as YYYY-MM-DD
 */
const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Get the day before a MM-DD date within a given year
 */
const getDayBefore = (year: number, mmdd: string): string => {
    const parts = mmdd.split('-');
    const d = new Date(year, parseInt(parts[0]) - 1, parseInt(parts[1]));
    d.setDate(d.getDate() - 1);
    return formatDate(d);
};

/**
 * Expand a start date into N consecutive days
 */
const expandDays = (year: number, mmdd: string, count: number, name: string) => {
    const start = new Date(year, parseInt(mmdd.split('-')[0]) - 1, parseInt(mmdd.split('-')[1]));
    const result = [];
    for (let i = 0; i < count; i++) {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        const dayLabel = count > 1 && i > 0 ? `${name} (${i + 1}. Gün)` : name;
        result.push({ date: formatDate(d), name: dayLabel });
    }
    return result;
};

/**
 * Get all public holidays for a given year, including all days of multi-day holidays
 * Includes arife (half-day before each bayram, counted as full day for business day calc)
 * @param {number} year
 * @returns {Array<string>} Array of date strings 'YYYY-MM-DD'
 */
const getHolidayDates = (year: number): string[] => {
    const dates = new Set<string>();

    // Fixed holidays
    FIXED_HOLIDAYS.forEach(h => dates.add(`${year}-${h.md}`));

    // Islamic holidays + arife days
    const islamic = ISLAMIC_HOLIDAYS[year];
    if (islamic) {
        // Ramazan arife + bayram
        dates.add(getDayBefore(year, islamic.ramazan));
        expandDays(year, islamic.ramazan, 3, '').forEach(h => dates.add(h.date));

        // Kurban arife + bayram
        dates.add(getDayBefore(year, islamic.kurban));
        expandDays(year, islamic.kurban, 4, '').forEach(h => dates.add(h.date));

        // Second Ramazan in same year (e.g., 2033)
        if (islamic.ramazan2) {
            dates.add(getDayBefore(year, islamic.ramazan2));
            expandDays(year, islamic.ramazan2, 3, '').forEach(h => dates.add(h.date));
        }
    }

    return Array.from(dates).sort();
};

// Cache holiday dates per year for performance
const holidayCache: Record<number, Set<string>> = {}

const getHolidaysForYear = (year: number): Set<string> => {
    if (!holidayCache[year]) {
        holidayCache[year] = new Set(getHolidayDates(year));
    }
    return holidayCache[year];
};

/**
 * Check if a date is a Turkish public holiday
 * @param {Date|string} date
 * @returns {boolean}
 */
const isHoliday = (date: Date | string): boolean => {
    const d = new Date(date);
    const year = d.getFullYear();
    const dateString = formatDate(d);
    return getHolidaysForYear(year).has(dateString);
};

/**
 * Check if a date string (YYYY-MM-DD) is a Turkish public holiday
 * @param {string} dateString ISO date string like '2026-03-20'
 * @returns {boolean}
 */
const isHolidayString = (dateString: string): boolean => {
    if (!dateString) return false;
    const year = parseInt(dateString.substring(0, 4));
    return getHolidaysForYear(year).has(dateString);
};

/**
 * Get all public holidays for a given year with names
 * @param {number} year
 * @returns {Array<{date: string, name: string}>}
 */
const getHolidays = (year: number): Array<{date: string, name: string}> => {
    const result = [];

    // Fixed holidays
    FIXED_HOLIDAYS.forEach(h => {
        result.push({ date: `${year}-${h.md}`, name: h.name });
    });

    // Islamic holidays + arife days
    const islamic = ISLAMIC_HOLIDAYS[year];
    if (islamic) {
        result.push({ date: getDayBefore(year, islamic.ramazan), name: 'Ramazan Bayramı Arife' });
        result.push(...expandDays(year, islamic.ramazan, 3, 'Ramazan Bayramı'));

        result.push({ date: getDayBefore(year, islamic.kurban), name: 'Kurban Bayramı Arife' });
        result.push(...expandDays(year, islamic.kurban, 4, 'Kurban Bayramı'));

        if (islamic.ramazan2) {
            result.push({ date: getDayBefore(year, islamic.ramazan2), name: 'Ramazan Bayramı Arife' });
            result.push(...expandDays(year, islamic.ramazan2, 3, 'Ramazan Bayramı'));
        }
    }

    return result.sort((a, b) => a.date.localeCompare(b.date));
};

export {  isHoliday, getHolidays, isHolidayString, getHolidayDates  };
